/**
 * Product Configuration Registry
 * 
 * Central registry for all product-related configurations.
 * This module exports product configs and provides utilities
 * for working with perils, currencies, and agricultural activities.
 */

import { productConfig, perils, currencies, activities } from './product';
import type { 
  PerilConfig, 
  CurrencyConfig, 
  ActivityConfig, 
  PerilOption, 
  CurrencyOption, 
  ActivityOption,
  ActivityCategory,
  ActivityGroup
} from './types';

/**
 * Get all available perils
 * @returns Array of peril configurations
 */
export function getPerils(): PerilConfig[] {
  return perils;
}

/**
 * Get all available currencies
 * @returns Array of currency configurations
 */
export function getCurrencies(): CurrencyConfig[] {
  return currencies;
}

/**
 * Get all available activities
 * @returns Array of activity configurations
 */
export function getActivities(): ActivityConfig[] {
  return activities;
}

/**
 * Get peril configuration by ID
 * @param perilId Peril identifier
 * @returns Peril configuration or undefined if not found
 */
export function getPerilById(perilId: string): PerilConfig | undefined {
  return perils.find(p => p.id === perilId);
}

/**
 * Get currency configuration by code
 * @param currencyCode Currency code (e.g., 'USD', 'ZWG')
 * @returns Currency configuration or undefined if not found
 */
export function getCurrencyByCode(currencyCode: string): CurrencyConfig | undefined {
  return currencies.find(c => c.code === currencyCode);
}

/**
 * Get activity configuration by ID
 * @param activityId Activity identifier
 * @returns Activity configuration or undefined if not found
 */
export function getActivityById(activityId: string): ActivityConfig | undefined {
  return activities.find(a => a.id === activityId);
}

/**
 * Get peril options for form selection (showing common perils first)
 * @returns Array of peril options sorted by usage frequency
 */
export function getPerilOptions(): PerilOption[] {
  return perils
    .sort((a, b) => {
      // Sort by common first, then alphabetically
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return a.name.localeCompare(b.name);
    })
    .map(peril => ({
      value: peril.id,
      label: peril.name,
      description: peril.description,
      common: peril.common
    }));
}

/**
 * Get currency options for form selection (showing common currencies first)
 * @returns Array of currency options sorted by usage frequency
 */
export function getCurrencyOptions(): CurrencyOption[] {
  return currencies
    .sort((a, b) => {
      // Sort by common first, then alphabetically
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return a.name.localeCompare(b.name);
    })
    .map(currency => ({
      value: currency.code,
      label: `${currency.name} (${currency.code})`,
      symbol: currency.symbol,
      common: currency.common
    }));
}

/**
 * Get activity options for form selection (showing common activities first)
 * @param category Optional category filter
 * @returns Array of activity options sorted by usage frequency and category
 */
export function getActivityOptions(category?: ActivityCategory): ActivityOption[] {
  let filteredActivities = activities;
  
  if (category && category !== 'all') {
    filteredActivities = activities.filter(a => a.category === category);
  }
  
  return filteredActivities
    .sort((a, b) => {
      // Sort by common first, then alphabetically
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return a.name.localeCompare(b.name);
    })
    .map(activity => ({
      value: activity.id,
      label: activity.name,
      category: activity.category,
      common: activity.common
    }));
}

/**
 * Get activities grouped by category
 * @returns Array of activity groups
 */
export function getActivitiesByCategory(): ActivityGroup[] {
  const groups: ActivityGroup[] = [
    {
      category: 'crops',
      label: 'Crop Production',
      activities: activities.filter(a => a.category === 'crops')
    },
    {
      category: 'livestock',
      label: 'Livestock Production',
      activities: activities.filter(a => a.category === 'livestock')
    },
    {
      category: 'other',
      label: 'Other Activities',
      activities: activities.filter(a => a.category === 'other')
    }
  ];
  
  // Sort activities within each group (common first, then alphabetical)
  groups.forEach(group => {
    group.activities.sort((a, b) => {
      if (a.common && !b.common) return -1;
      if (!a.common && b.common) return 1;
      return a.name.localeCompare(b.name);
    });
  });
  
  return groups;
}

/**
 * Get common perils (frequently used ones)
 * @returns Array of commonly used perils
 */
export function getCommonPerils(): PerilConfig[] {
  return perils.filter(p => p.common);
}

/**
 * Get common currencies (frequently used ones)
 * @returns Array of commonly used currencies
 */
export function getCommonCurrencies(): CurrencyConfig[] {
  return currencies.filter(c => c.common);
}

/**
 * Get common activities (frequently used ones)
 * @returns Array of commonly used activities
 */
export function getCommonActivities(): ActivityConfig[] {
  return activities.filter(a => a.common);
}

/**
 * Convert peril names to IDs for backward compatibility
 * @param perilNames Array of peril names
 * @returns Array of peril IDs
 */
export function convertPerilNamesToIds(perilNames: string[]): string[] {
  return perilNames.map(name => {
    const peril = perils.find(p => p.name === name);
    return peril ? peril.id : name.toLowerCase().replace(/[^a-z0-9]/g, '-');
  });
}

/**
 * Convert peril IDs to names for display
 * @param perilIds Array of peril IDs
 * @returns Array of peril names
 */
export function convertPerilIdsToNames(perilIds: string[]): string[] {
  return perilIds.map(id => {
    const peril = perils.find(p => p.id === id);
    return peril ? peril.name : id;
  });
}

/**
 * Validate if perils exist in configuration
 * @param perilIds Array of peril IDs to validate
 * @returns Object with valid and invalid peril IDs
 */
export function validatePerils(perilIds: string[]): { valid: string[], invalid: string[] } {
  const valid: string[] = [];
  const invalid: string[] = [];
  
  perilIds.forEach(id => {
    if (perils.some(p => p.id === id)) {
      valid.push(id);
    } else {
      invalid.push(id);
    }
  });
  
  return { valid, invalid };
}

/**
 * Format currency value with proper symbol and decimals
 * @param amount Numeric amount
 * @param currencyCode Currency code
 * @returns Formatted currency string
 */
export function formatCurrency(amount: number, currencyCode: string): string {
  const currency = getCurrencyByCode(currencyCode);
  if (!currency) {
    return `${amount} ${currencyCode}`;
  }
  
  const formatted = amount.toFixed(currency.decimals);
  return `${currency.symbol}${formatted}`;
}

// Re-export types and configurations for easy access
export * from './types';
export { productConfig, perils, currencies, activities };

// Default export for convenience
export default {
  getPerils,
  getCurrencies,
  getActivities,
  getPerilById,
  getCurrencyByCode,
  getActivityById,
  getPerilOptions,
  getCurrencyOptions,
  getActivityOptions,
  getActivitiesByCategory,
  getCommonPerils,
  getCommonCurrencies,
  getCommonActivities,
  convertPerilNamesToIds,
  convertPerilIdsToNames,
  validatePerils,
  formatCurrency
};
